import { useEffect, useCallback, useRef } from 'react';
import { useAnalytics } from './useAnalytics';

/**
 * 性能监控Hook
 * 监控页面性能指标，包括加载时间、FCP、LCP、CLS等
 */
export const usePerformanceMonitor = (options = {}) => {
  const { trackPerformance } = useAnalytics();
  const metricsRef = useRef({});
  const observerRef = useRef(null);
  
  const {
    enableWebVitals = true,
    enableResourceTiming = true,
    enableNavigationTiming = true,
    enableMemoryMonitoring = true,
    reportInterval = 30000, // 30秒报告一次
    thresholds = {
      fcp: 2000,    // First Contentful Paint
      lcp: 2500,    // Largest Contentful Paint
      fid: 100,     // First Input Delay
      cls: 0.1,     // Cumulative Layout Shift
      ttfb: 600     // Time to First Byte
    }
  } = options;

  // 获取导航时间
  const getNavigationTiming = useCallback(() => {
    if (!performance.getEntriesByType) return null;
    
    const navigation = performance.getEntriesByType('navigation')[0];
    if (!navigation) return null;

    return {
      dns: navigation.domainLookupEnd - navigation.domainLookupStart,
      tcp: navigation.connectEnd - navigation.connectStart,
      ssl: navigation.secureConnectionStart > 0 ? 
           navigation.connectEnd - navigation.secureConnectionStart : 0,
      ttfb: navigation.responseStart - navigation.requestStart,
      download: navigation.responseEnd - navigation.responseStart,
      domParse: navigation.domContentLoadedEventEnd - navigation.responseEnd,
      domReady: navigation.domContentLoadedEventEnd - navigation.navigationStart,
      loadComplete: navigation.loadEventEnd - navigation.navigationStart
    };
  }, []);

  // 获取资源时间
  const getResourceTiming = useCallback(() => {
    if (!performance.getEntriesByType) return [];
    
    const resources = performance.getEntriesByType('resource');
    return resources.map(resource => ({
      name: resource.name,
      type: resource.initiatorType,
      size: resource.transferSize || 0,
      duration: resource.duration,
      startTime: resource.startTime
    }));
  }, []);

  // 获取内存信息
  const getMemoryInfo = useCallback(() => {
    if (!performance.memory) return null;
    
    return {
      used: performance.memory.usedJSHeapSize,
      total: performance.memory.totalJSHeapSize,
      limit: performance.memory.jsHeapSizeLimit,
      usage: (performance.memory.usedJSHeapSize / performance.memory.jsHeapSizeLimit) * 100
    };
  }, []);

  // 监控Web Vitals
  const monitorWebVitals = useCallback(() => {
    if (!enableWebVitals) return;

    // First Contentful Paint (FCP)
    const observeFCP = () => {
      const observer = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        entries.forEach((entry) => {
          if (entry.name === 'first-contentful-paint') {
            const fcp = entry.startTime;
            metricsRef.current.fcp = fcp;
            
            trackPerformance('fcp', fcp, {
              isGood: fcp <= thresholds.fcp,
              threshold: thresholds.fcp
            });
          }
        });
      });
      
      observer.observe({ entryTypes: ['paint'] });
      return observer;
    };

    // Largest Contentful Paint (LCP)
    const observeLCP = () => {
      const observer = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        const lastEntry = entries[entries.length - 1];
        
        if (lastEntry) {
          const lcp = lastEntry.startTime;
          metricsRef.current.lcp = lcp;
          
          trackPerformance('lcp', lcp, {
            isGood: lcp <= thresholds.lcp,
            threshold: thresholds.lcp,
            element: lastEntry.element?.tagName
          });
        }
      });
      
      observer.observe({ entryTypes: ['largest-contentful-paint'] });
      return observer;
    };

    // First Input Delay (FID)
    const observeFID = () => {
      const observer = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        entries.forEach((entry) => {
          const fid = entry.processingStart - entry.startTime;
          metricsRef.current.fid = fid;
          
          trackPerformance('fid', fid, {
            isGood: fid <= thresholds.fid,
            threshold: thresholds.fid,
            eventType: entry.name
          });
        });
      });
      
      observer.observe({ entryTypes: ['first-input'] });
      return observer;
    };

    // Cumulative Layout Shift (CLS)
    const observeCLS = () => {
      let clsValue = 0;
      
      const observer = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        entries.forEach((entry) => {
          if (!entry.hadRecentInput) {
            clsValue += entry.value;
          }
        });
        
        metricsRef.current.cls = clsValue;
        
        trackPerformance('cls', clsValue, {
          isGood: clsValue <= thresholds.cls,
          threshold: thresholds.cls
        });
      });
      
      observer.observe({ entryTypes: ['layout-shift'] });
      return observer;
    };

    // 启动所有观察器
    const observers = [
      observeFCP(),
      observeLCP(),
      observeFID(),
      observeCLS()
    ].filter(Boolean);

    return () => {
      observers.forEach(observer => observer.disconnect());
    };
  }, [enableWebVitals, trackPerformance, thresholds]);

  // 监控长任务
  const monitorLongTasks = useCallback(() => {
    if (!window.PerformanceObserver) return;

    const observer = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      entries.forEach((entry) => {
        trackPerformance('long_task', entry.duration, {
          startTime: entry.startTime,
          attribution: entry.attribution?.[0]?.name
        });
      });
    });

    observer.observe({ entryTypes: ['longtask'] });
    return () => observer.disconnect();
  }, [trackPerformance]);

  // 监控用户交互性能
  const monitorInteractionPerformance = useCallback(() => {
    let interactionCount = 0;
    let totalInteractionTime = 0;

    const handleInteraction = (event) => {
      const startTime = performance.now();
      
      requestAnimationFrame(() => {
        const endTime = performance.now();
        const duration = endTime - startTime;
        
        interactionCount++;
        totalInteractionTime += duration;
        
        // 如果交互时间过长，记录
        if (duration > 50) {
          trackPerformance('slow_interaction', duration, {
            eventType: event.type,
            target: event.target?.tagName
          });
        }
      });
    };

    // 监听各种交互事件
    const events = ['click', 'keydown', 'scroll', 'touchstart'];
    events.forEach(eventType => {
      document.addEventListener(eventType, handleInteraction, { passive: true });
    });

    return () => {
      events.forEach(eventType => {
        document.removeEventListener(eventType, handleInteraction);
      });
      
      // 报告平均交互时间
      if (interactionCount > 0) {
        trackPerformance('avg_interaction_time', totalInteractionTime / interactionCount);
      }
    };
  }, [trackPerformance]);

  // 定期报告性能指标
  const startPeriodicReporting = useCallback(() => {
    const interval = setInterval(() => {
      // 导航时间
      if (enableNavigationTiming) {
        const navTiming = getNavigationTiming();
        if (navTiming) {
          Object.entries(navTiming).forEach(([key, value]) => {
            trackPerformance(`nav_${key}`, value);
          });
        }
      }

      // 资源时间
      if (enableResourceTiming) {
        const resources = getResourceTiming();
        const totalSize = resources.reduce((sum, r) => sum + r.size, 0);
        const avgDuration = resources.length > 0 ? 
          resources.reduce((sum, r) => sum + r.duration, 0) / resources.length : 0;
        
        trackPerformance('total_resource_size', totalSize);
        trackPerformance('avg_resource_duration', avgDuration);
        trackPerformance('resource_count', resources.length);
      }

      // 内存信息
      if (enableMemoryMonitoring) {
        const memory = getMemoryInfo();
        if (memory) {
          trackPerformance('memory_usage', memory.usage);
          trackPerformance('memory_used', memory.used);
        }
      }

      // 当前性能指标
      trackPerformance('current_metrics', 1, metricsRef.current);
      
    }, reportInterval);

    return () => clearInterval(interval);
  }, [
    enableNavigationTiming,
    enableResourceTiming, 
    enableMemoryMonitoring,
    reportInterval,
    getNavigationTiming,
    getResourceTiming,
    getMemoryInfo,
    trackPerformance
  ]);

  // 检测性能问题
  const detectPerformanceIssues = useCallback(() => {
    const issues = [];
    const metrics = metricsRef.current;

    // 检查各项指标
    if (metrics.fcp && metrics.fcp > thresholds.fcp) {
      issues.push({
        type: 'slow_fcp',
        value: metrics.fcp,
        threshold: thresholds.fcp,
        severity: metrics.fcp > thresholds.fcp * 2 ? 'high' : 'medium'
      });
    }

    if (metrics.lcp && metrics.lcp > thresholds.lcp) {
      issues.push({
        type: 'slow_lcp',
        value: metrics.lcp,
        threshold: thresholds.lcp,
        severity: metrics.lcp > thresholds.lcp * 2 ? 'high' : 'medium'
      });
    }

    if (metrics.cls && metrics.cls > thresholds.cls) {
      issues.push({
        type: 'high_cls',
        value: metrics.cls,
        threshold: thresholds.cls,
        severity: metrics.cls > thresholds.cls * 3 ? 'high' : 'medium'
      });
    }

    // 报告性能问题
    if (issues.length > 0) {
      trackPerformance('performance_issues', issues.length, {
        issues: issues
      });
    }

    return issues;
  }, [thresholds, trackPerformance]);

  // 初始化性能监控
  useEffect(() => {
    const cleanupFunctions = [];

    // 启动各种监控
    cleanupFunctions.push(monitorWebVitals());
    cleanupFunctions.push(monitorLongTasks());
    cleanupFunctions.push(monitorInteractionPerformance());
    cleanupFunctions.push(startPeriodicReporting());

    // 页面卸载时检测性能问题
    const handleBeforeUnload = () => {
      detectPerformanceIssues();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    cleanupFunctions.push(() => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    });

    return () => {
      cleanupFunctions.forEach(cleanup => {
        if (typeof cleanup === 'function') {
          cleanup();
        }
      });
    };
  }, [
    monitorWebVitals,
    monitorLongTasks,
    monitorInteractionPerformance,
    startPeriodicReporting,
    detectPerformanceIssues
  ]);

  // 返回当前性能指标
  const getCurrentMetrics = useCallback(() => {
    return {
      ...metricsRef.current,
      navigation: getNavigationTiming(),
      memory: getMemoryInfo(),
      resources: getResourceTiming()
    };
  }, [getNavigationTiming, getMemoryInfo, getResourceTiming]);

  return {
    getCurrentMetrics,
    detectPerformanceIssues
  };
};

export default usePerformanceMonitor;
